import { useState } from 'react';
import {
    Alert,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableContainer
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    ActionType,
    ConsentResponse,
    GetConsentsResponse,
    UserRoles
} from '@/models/company';
import { useFormatting } from '@/utils/formatting';
import {
    TableCell,
    TableHeader,
    TableRow,
    TableHeaderCell
} from '@/components/shared/styles/Table';
import { TableCard } from '@/components/shared/Card';
import CardHeader from '@/components/shared/CardHeader';
import Chip from '@/components/shared/Chip';
import {
    OutlinedSecondaryButton,
    PrimaryButton
} from '@/components/shared/Button';
import { UploadLegalDocumentDialog } from './UploadLegalDocumentDialog';
import { ButtonContainer } from '@/pages/company/PaymentSetting';
import { useGetConsents } from '@/services/usersQuery';
import { useUsersMutations } from '@/services/usersMutations';
import { useHandleResponse } from '@/lib/hooks/useHandleResponse';
import { useUser } from '@/contexts/user/UserContext';
import { AcceptConsentDialog } from './AcceptConsentDialog';
import { ConfirmationOverlay } from '@/pages/company/disable/ConfirmationOverlay';
import { TextMDSemiBold, TextSMRegular } from '@/typography/Typography';
import { TrashIcon } from '@/icons/TrashIcon';

type DisplayStatus = ActionType | 'Archived';

type ProcessedConsent = ConsentResponse & { displayStatus: DisplayStatus };

const processConsents = (consents: ConsentResponse[]): ProcessedConsent[] => {
    // Sort newest first so per-type "latest" is unambiguous
    const sorted = [...consents].sort(
        (a, b) =>
            new Date(b.performedAt).getTime() -
            new Date(a.performedAt).getTime()
    );

    // Find the performedAt of the latest Accepted entry per consentType
    const latestAcceptedAt = new Map<string, Date>();
    for (const consent of sorted) {
        if (
            consent.actionType === 'Accepted' &&
            !latestAcceptedAt.has(consent.consentType)
        ) {
            latestAcceptedAt.set(
                consent.consentType,
                new Date(consent.performedAt)
            );
        }
    }

    const seenTypes = new Set<string>();
    const result: ProcessedConsent[] = [];

    for (const consent of sorted) {
        const isFirst = !seenTypes.has(consent.consentType);
        seenTypes.add(consent.consentType);

        if (isFirst) {
            // Newest entry for this type — always show as-is
            result.push({ ...consent, displayStatus: consent.actionType });
        } else {
            // Older entry — only relevant if it was accepted AND there is a
            // newer accepted version superseding it
            const latestAccepted = latestAcceptedAt.get(consent.consentType);
            const isSupersededByAccepted =
                latestAccepted !== undefined &&
                new Date(consent.performedAt) < latestAccepted;

            if (consent.actionType === 'Accepted' && isSupersededByAccepted) {
                result.push({ ...consent, displayStatus: 'Archived' });
            }
            // Postponed / NotSigned older versions are hidden entirely
        }
    }

    return result;
};

const getStatusColor = (status: DisplayStatus) => {
    switch (status) {
        case 'Accepted':
            return 'success';
        case 'Declined':
            return 'error';
        case 'Archived':
            return 'muted';
        case 'NotSigned':
        case 'Postponed':
        default:
            return 'warning';
    }
};

export const Status = ({ status }: { status: DisplayStatus }) => {
    return <Chip color={getStatusColor(status) as any} label={status} />;
};

export const ConsentsTable = () => {
    const { t } = useTranslation();
    const [openUploadDialog, setOpenUploadDialog] = useState(false);
    const [acceptConsent, setAcceptConsent] = useState<ConsentResponse | null>(
        null
    );
    const [showArchived, setShowArchived] = useState(false);
    const [deleteConsentItem, setDeleteConsentItem] =
        useState<ConsentResponse | null>(null);
    const { formatDateTime } = useFormatting();
    const { data, error } = useGetConsents();
    const { deleteTermsVersion } = useUsersMutations();

    useHandleResponse({ error });

    const consents = data?.data as GetConsentsResponse | undefined;
    const processedConsents: ProcessedConsent[] = consents
        ? processConsents(consents.consents)
        : [];
    const hasArchived = processedConsents.some(
        (c) => c.displayStatus === 'Archived'
    );
    const sortByDate = (a: ProcessedConsent, b: ProcessedConsent) =>
        new Date(b.performedAt).getTime() - new Date(a.performedAt).getTime();

    const activeConsents = processedConsents
        .filter((c) => c.displayStatus !== 'Archived')
        .sort(sortByDate);
    const archivedConsents = processedConsents
        .filter((c) => c.displayStatus === 'Archived')
        .sort(sortByDate);
    const visibleConsents = showArchived
        ? [...activeConsents, ...archivedConsents]
        : activeConsents;

    const getTermsDocumentName = (consent: ConsentResponse) => {
        if (consent.termsVersionName) {
            return consent.termsVersionName;
        }
        switch (consent.consentType) {
            case 'Commercial':
                return t('license_agreement');
            case 'Data':
                return t('data_processing_agreement');
            case 'NewsletterAndUpdates':
                return t('marketing_updates');
            default:
                return consent.consentType;
        }
    };

    const getConsentTypeLabel = (consent: ConsentResponse) => {
        switch (consent.consentType) {
            case 'Commercial':
                return t('license_agreement');
            case 'Data':
                return t('data_processing_agreement');
            case 'NewsletterAndUpdates':
                return t('marketing_updates');
            default:
                return consent.consentType;
        }
    };

    const { user } = useUser();
    const isSuperAdmin = user.role === UserRoles.SuperAdmin;

    const handleDeleteConfirm = () => {
        if (!deleteConsentItem) {
            return;
        }
        // Close only on success — on error the overlay stays open (alongside the
        // mutation's error toast) so the admin can retry in context.
        deleteTermsVersion.mutate(deleteConsentItem.termsVersionId, {
            onSuccess: () => setDeleteConsentItem(null)
        });
    };

    return (
        <>
            {error && <Alert severity="error">{error.message}</Alert>}

            <TableCard>
                <CardHeader
                    label={
                        <Stack direction={'row'} alignItems={'center'}>
                            {t('legal_documents')}
                        </Stack>
                    }
                    actions={
                        hasArchived ? (
                            <OutlinedSecondaryButton
                                size="small"
                                onClick={() => setShowArchived((prev) => !prev)}
                            >
                                {showArchived
                                    ? t('hide_archived_documents')
                                    : t('show_archived_documents')}
                            </OutlinedSecondaryButton>
                        ) : undefined
                    }
                ></CardHeader>
                <TableContainer>
                    <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell
                                    sx={{
                                        fontWeight: 'bold',
                                        width: '35%'
                                    }}
                                >
                                    {t('document_column')}
                                </TableHeaderCell>
                                <TableHeaderCell
                                    align="center"
                                    sx={{ fontWeight: 'bold' }}
                                >
                                    {t('action_column')}
                                </TableHeaderCell>
                                <TableHeaderCell sx={{ fontWeight: 'bold' }}>
                                    {t('type_column')}
                                </TableHeaderCell>
                                <TableHeaderCell sx={{ fontWeight: 'bold' }}>
                                    {t('changed_at_column')}
                                </TableHeaderCell>
                                <TableHeaderCell
                                    align="right"
                                    sx={{ fontWeight: 'bold' }}
                                >
                                    {t('accepted_signed_by_column')}
                                </TableHeaderCell>
                                {isSuperAdmin && (
                                    <TableHeaderCell align="right" />
                                )}
                            </TableRow>
                        </TableHeader>

                        <TableBody>
                            {visibleConsents.map((consent) => {
                                return (
                                    <TableRow key={consent.consentId}>
                                        <TableCell>
                                            {consent.termsVersionUrl ? (
                                                <a
                                                    href={
                                                        consent.termsVersionUrl
                                                    }
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    {getTermsDocumentName(
                                                        consent
                                                    )}
                                                </a>
                                            ) : (
                                                <>
                                                    {getTermsDocumentName(
                                                        consent
                                                    )}
                                                </>
                                            )}
                                        </TableCell>
                                        <TableCell align="left">
                                            <Status
                                                status={consent.displayStatus}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {getConsentTypeLabel(consent)}
                                        </TableCell>
                                        <TableCell>
                                            {formatDateTime(
                                                consent.performedAt
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {consent.displayStatus ===
                                                'NotSigned' ||
                                            consent.displayStatus ===
                                                'Postponed' ? (
                                                <a
                                                    href="#"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setAcceptConsent(
                                                            consent
                                                        );
                                                    }}
                                                >
                                                    {t('accept_now')}
                                                </a>
                                            ) : (
                                                (consent.performedByEmail ??
                                                consent.performedByName)
                                            )}
                                        </TableCell>
                                        {isSuperAdmin && (
                                            <TableCell align="right">
                                                <IconButton
                                                    aria-label={t(
                                                        'delete_document'
                                                    )}
                                                    title={t('delete_document')}
                                                    onClick={() =>
                                                        setDeleteConsentItem(
                                                            consent
                                                        )
                                                    }
                                                >
                                                    <TrashIcon size={16} />
                                                </IconButton>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                {consents && processedConsents.length === 0 && (
                    <Alert severity="info">{t('no_accepted_documents')}</Alert>
                )}
                {user.role == UserRoles.SuperAdmin && (
                    <ButtonContainer style={{ borderTop: 'none' }}>
                        <PrimaryButton
                            onClick={() => setOpenUploadDialog(true)}
                            sx={{ marginLeft: '6px !important' }}
                            size="medium"
                        >
                            {t('upload_company_document_button')}
                        </PrimaryButton>
                    </ButtonContainer>
                )}
            </TableCard>

            <UploadLegalDocumentDialog
                open={openUploadDialog}
                onClose={() => setOpenUploadDialog(false)}
            />

            {acceptConsent && (
                <AcceptConsentDialog
                    consent={acceptConsent}
                    allConsents={consents?.consents ?? []}
                    onClose={() => setAcceptConsent(null)}
                />
            )}

            <ConfirmationOverlay
                danger
                open={!!deleteConsentItem}
                loading={deleteTermsVersion.isPending}
                displayAreYouSureText={false}
                continueText={t('delete')}
                onCancel={() => setDeleteConsentItem(null)}
                onContinue={handleDeleteConfirm}
                p1={
                    <TextMDSemiBold className="text-center">
                        {t('delete_document_confirm_title')}
                    </TextMDSemiBold>
                }
                p2={
                    <TextSMRegular mt={1} className="text-center">
                        {t('delete_document_confirm_content', {
                            name: deleteConsentItem
                                ? getTermsDocumentName(deleteConsentItem)
                                : ''
                        })}
                    </TextSMRegular>
                }
            />
        </>
    );
};
